import type React from 'react';

import {
  appointmentDataWithResetAtom,
  getCurrentFields,
  getCurrentTemplate,
  getFieldValue,
  isRecorderSheetOpenAtom,
  selectedNoteIdAtom,
  uiStatusAtom,
  voiceNotesSourceAtom,
} from '@repo/videa-notes';
import { valueForDisplay } from '@repo/videa-components/src/ui/variable-textarea/useContentEditable';
import { formatInTimeZone } from 'date-fns-tz';
import { useSetAtom } from 'jotai';

import type { User } from '@/@types/global';
import type { Appointment } from '@/react-query/useAppointments';
import type {
  VideaNoteSectionFieldsSnapshot,
  VideaNoteTemplateSectionSnapshot,
} from '@/react-query/videa-notes/types';
import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';

import {
  type EditedFields,
  type NoteUpdatePayload,
  VideaNotesUIState,
  type VoiceNotesSource,
} from './types';

export const BLURB_DELIMITER = '\n\n==========\n\n';

// Re-export for backward compatibility
export { VideaNotesUIState };

// Extend the original Appointment type to support both regular appointments and standalone visits
export type VideaNotesAppointment = Appointment & {
  appointment: Appointment['appointment'] & {
    /**
     * Make unifiedId optional to support standalone visits
     */
    unifiedId?: string;
    /**
     * Add support for standalone patient visits
     */
    standalonePatientVisitId?: string;
    /**
     * Timestamp when the note was created (for recent notes sorting)
     */
    noteCreatedAt?: string | null;
    /**
     * Specific note represented by a recent-notes table row.
     */
    noteId?: string | null;
  };
};

export const useOpenVideaNotes = () => {
  const setAppointmentDataForVideaNotes = useSetAtom(
    appointmentDataWithResetAtom,
  );
  const setIsVideaNotesSheetOpen = useSetAtom(isRecorderSheetOpenAtom);
  const setSelectedNoteId = useSetAtom(selectedNoteIdAtom);
  const setUiStatus = useSetAtom(uiStatusAtom);
  const setVoiceNotesSource = useSetAtom(voiceNotesSourceAtom);

  return (
    user: User,
    appointment: VideaNotesAppointment,
    uiState: VideaNotesUIState,
    voiceNotesSource: VoiceNotesSource,
    selectedNoteId?: string,
  ) => {
    // Set appointment data first (this will reset UI state to AUDIO_CAPTURE)
    const appointmentData = {
      appointmentStartsAt: appointment.appointment.start,
      patient: appointment.patient,
      estimatedVisitId: appointment.appointment.estimatedVisitId,
      standalonePatientVisitId:
        appointment.appointment.standalonePatientVisitId,
      unifiedId: appointment.appointment.unifiedId,
      loggedInUser: user.username ?? '',
    };

    setAppointmentDataForVideaNotes(appointmentData);

    setSelectedNoteId(selectedNoteId);

    // Set the UI state after appointment data
    setUiStatus(uiState);

    setIsVideaNotesSheetOpen(true);

    setVoiceNotesSource(voiceNotesSource);
  };
};

/**
 * Formats the patient consent blurb for display.
 */
export const formatConsentBlurb = (consent: {
  id: string;
  fullName?: string | null;
  dob?: string | null;
  createdAtISO?: Date | string | null;
}): string => {
  try {
    const createdAt = consent.createdAtISO
      ? consent.createdAtISO instanceof Date
        ? consent.createdAtISO
        : new Date(consent.createdAtISO)
      : null;
    const isValidDate =
      createdAt instanceof Date && !isNaN(createdAt.getTime());

    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const consentGrantedAt = isValidDate
      ? formatInTimeZone(
          createdAt,
          userTimeZone,
          'MMMM d, yyyy @ hh:mm:ss a zzz',
        )
      : 'An unknown date';

    return `On ${consentGrantedAt}, Patient gave verbal consent for audio recording for documentation during this encounter. Purpose explained and opt-out offered; consent granted.\nConsent Ref: [${consent.id}]`;
  } catch (e) {
    console.error('Error formatting consent date:', e);
    return `Verbal consent for recording was granted, but the date could not be parsed. Consent Ref: [${consent.id}].`;
  }
};

/**
 * Removes the consent blurb from note summary if writeback is disabled.
 */
export const getNoteSummaryWithoutBlurb = (
  noteSummary: string,
  hasWritebackEnabled: boolean,
): string => {
  return hasWritebackEnabled
    ? noteSummary
    : noteSummary.split(BLURB_DELIMITER)[0];
};

/**
 * Creates a map of variable names to their current values from fields and editedFields.
 * This is the single source of truth for variable values.
 */
const buildVariableValueMap = (
  fields: VideaNoteSectionFieldsSnapshot[] | null | undefined,
  editedFields?: EditedFields,
): Map<string, string> => {
  const variableMap = new Map<string, string>();

  // First, populate from current fields
  if (fields?.length) {
    const allFields = fields.flatMap((section) => section.fields);
    for (const field of allFields) {
      if (!variableMap.has(field.key)) {
        variableMap.set(field.key, getFieldValue(field));
      }
    }
  }

  // Then, override with any unsaved edits from editedFields
  if (editedFields?.length) {
    const allEditedFields = editedFields.flatMap((section) => section.fields);
    for (const field of allEditedFields) {
      if (field.editedValue !== null) {
        variableMap.set(field.key, String(field.editedValue));
      }
    }
  }

  return variableMap;
};

/**
 * Renders a template string by replacing {{variable}} placeholders with field values.
 * Uses the same variable value resolution logic as extractVariableDefinitions.
 * Optionally merges in unsaved edits from editedFields.
 */
export const renderTemplateWithVariables = (
  template: string,
  fields: VideaNoteSectionFieldsSnapshot[] | null | undefined,
  editedFields?: EditedFields,
): string => {
  if (!template) return template;

  // Use the shared variable value map builder
  const variableMap = buildVariableValueMap(fields, editedFields);

  // Replace all {{variable}} placeholders with display-safe values (multi-select
  // uses internal \u001D; valueForDisplay converts to ", " for API/PMS).
  return template.replace(/\{\{([\w_]+)\}\}/g, (match, varName) => {
    const raw = variableMap.get(varName);
    return raw != null ? valueForDisplay(raw) : '___';
  });
};

/**
 * Gets the original content without blurb for a note.
 */
export const getOriginalContent = (
  noteWithTemplateData: ClinicalNote | undefined,
  selectedNote: ClinicalNote | undefined,
): string => {
  const currentTemplate = getCurrentTemplate(noteWithTemplateData);
  const isTemplateNote = currentTemplate && currentTemplate.length > 0;
  return isTemplateNote
    ? noteWithTemplateData?.originalTemplate?.[0]?.bodyTemplate ||
        selectedNote?.summary ||
        ''
    : selectedNote?.summary || '';
};

/**
 * Creates a note update payload for saving changes.
 */
export const createNoteUpdatePayload = (
  contentWithoutBlurb: string,
  isTemplateNote: boolean,
  includeFields: boolean,
  noteWithTemplateData: ClinicalNote | undefined,
  editedFields: EditedFields,
): NoteUpdatePayload => {
  const updatePayload: NoteUpdatePayload = {};

  if (isTemplateNote) {
    const currentTemplate = getCurrentTemplate(noteWithTemplateData);
    if (currentTemplate && currentTemplate.length > 0) {
      updatePayload.editedTemplate = [
        {
          sectionId: currentTemplate[0].sectionId,
          bodyTemplate: contentWithoutBlurb,
        },
      ];

      // For template notes, also include editedSummary with variables rendered
      const currentFields = getCurrentFields(noteWithTemplateData);
      updatePayload.editedSummary = renderTemplateWithVariables(
        contentWithoutBlurb,
        currentFields,
        editedFields,
      );

      // Include full fields structure for template notes
      updatePayload.fields = currentFields;
    }
  } else {
    updatePayload.editedSummary = contentWithoutBlurb;
  }

  if (includeFields && editedFields && editedFields.length > 0) {
    updatePayload.editedFields = editedFields;
  }

  return updatePayload;
};

/**
 * Creates a payload to clear all field edits.
 */
export const createClearFieldsPayload = (
  noteWithTemplateData: ClinicalNote | undefined,
): NoteUpdatePayload['editedFields'] => {
  const currentFields = getCurrentFields(noteWithTemplateData);
  if (!currentFields) return null;

  const fieldsToClear: NonNullable<NoteUpdatePayload['editedFields']> = [];

  currentFields.forEach((section) => {
    const clearedFields = section.fields.map((field) => ({
      key: field.key,
      editedValue: null,
    }));
    if (clearedFields.length > 0) {
      fieldsToClear.push({
        sectionId: section.sectionId,
        fields: clearedFields,
      });
    }
  });

  return fieldsToClear.length > 0 ? fieldsToClear : null;
};

/**
 * Detects changes in note content, template, or fields.
 * This centralizes the change detection logic used by both auto-save and save-before-close.
 *
 * @returns An object containing change detection results, or null if no note is provided.
 */
export const detectNoteChanges = (
  noteContent: string,
  noteWithTemplateData: ClinicalNote | undefined,
  editedFields: EditedFields,
  hasWritebackEnabled: boolean,
  prevContentRef: React.MutableRefObject<string>,
  prevTemplateRef: React.MutableRefObject<string | null>,
): {
  noteWithoutBlurb: string;
  currentTemplate: VideaNoteTemplateSectionSnapshot[] | null;
  isTemplateNote: boolean;
  templateChanged: boolean;
  contentChanged: boolean;
  fieldsChanged: boolean;
  hasChanges: boolean;
} | null => {
  const noteWithoutBlurb = getNoteSummaryWithoutBlurb(
    noteContent,
    hasWritebackEnabled,
  );

  const currentTemplate = getCurrentTemplate(noteWithTemplateData);
  const isTemplateNote = Boolean(currentTemplate && currentTemplate.length > 0);

  // Compare current content with last saved content to detect changes
  const templateChanged =
    isTemplateNote && prevTemplateRef.current !== noteWithoutBlurb;
  const contentChanged =
    !isTemplateNote && prevContentRef.current !== noteWithoutBlurb;
  const fieldsChanged = editedFields !== null && editedFields.length > 0;

  const hasChanges = templateChanged || contentChanged || fieldsChanged;

  return {
    noteWithoutBlurb,
    currentTemplate,
    isTemplateNote,
    templateChanged,
    contentChanged,
    fieldsChanged,
    hasChanges,
  };
};

export const isRecordLike = (
  value: unknown,
): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/** Returns true if the API response indicates empty transcript (422 EMPTY_TRANSCRIPT). */
export const isEmptyTranscriptError = (
  statusCode: number | undefined,
  responseData: unknown,
): boolean => {
  if (statusCode !== 422 || !isRecordLike(responseData)) return false;
  const data = responseData.data;
  if (!isRecordLike(data)) return false;
  const innerData = data.data;
  if (!isRecordLike(innerData)) return false;
  const detail = innerData.detail;
  return (
    isRecordLike(detail) &&
    typeof detail.code === 'string' &&
    detail.code === 'EMPTY_TRANSCRIPT'
  );
};
